import { Link } from "@tanstack/react-router";
import { Calculator, CheckCircle2, MousePointerClick } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export type CalculatorFaq = { q: string; a: string };
export type CalculatorRelated = { path: string; name: string; description: string; badge?: string; icon?: string };

export function HowToUseCalculator({ inputLabel = "Enter your details", resultLabel = "Review your result" }: { inputLabel?: string; resultLabel?: string }) {
  const steps = [
    { icon: MousePointerClick, title: inputLabel, text: "Use the labelled fields and units that match your situation." },
    { icon: Calculator, title: "Calculate instantly", text: "The result, visual comparison, and working update as values change." },
    { icon: CheckCircle2, title: resultLabel, text: "Check assumptions, then copy, print, or share the calculation." },
  ];
  return (
    <section className="mt-10" aria-labelledby="calculator-how-to">
      <h2 id="calculator-how-to" className="text-xl font-bold">How to Use This Calculator</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {steps.map((step, index) => (
          <div id={`step-${index + 1}`} key={step.title} className="rounded-lg border border-border bg-card p-4">
            <step.icon className="h-5 w-5 text-primary" aria-hidden="true" />
            <h3 className="mt-3 text-sm font-bold">{index + 1}. {step.title}</h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function CalculatorFaqAccordion({ faqs, title = "Frequently Asked Questions" }: { faqs: CalculatorFaq[]; title?: string }) {
  return (
    <section className="mt-10" aria-labelledby="calculator-faqs">
      <h2 id="calculator-faqs" className="text-xl font-bold">{title}</h2>
      <Accordion type="single" collapsible className="mt-3 rounded-lg border border-border bg-card px-4">
        {faqs.slice(0, 5).map((faq, index) => (
          <AccordionItem key={faq.q} value={`faq-${index}`}>
            <AccordionTrigger>{faq.q}</AccordionTrigger>
            <AccordionContent className="leading-relaxed text-muted-foreground">{faq.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export function RelatedCalculators({ items }: { items: CalculatorRelated[] }) {
  return (
    <section className="mt-10" aria-labelledby="related-calculators">
      <h2 id="related-calculators" className="text-xl font-bold">Related Calculators &amp; Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.slice(0, 8).map((item) => (
          <Link key={item.path} to={item.path as never} className="group rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/60">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xl" aria-hidden="true">{item.icon ?? "🧮"}</span>
              {item.badge ? <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{item.badge}</span> : null}
            </div>
            <h3 className="mt-3 text-sm font-bold group-hover:text-primary">{item.name}</h3>
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{item.description}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}